import { useCallback, useContext, useState } from "react";

import {
  AddSquareRegular,
  Delete24Regular,
  Edit24Regular,
  NavigationRegular,
} from "@fluentui/react-icons";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  makeStyles,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from "@fluentui/react-components";

import { translate } from "@/utils/translations";

import { Sidebar } from "../Sidebar";
import { AuthContext } from "../../utils/auth";
import { getThreadTitle } from "../../constants/threads";
import ChatbotContext from "../Chatbot/contexts/ChatbotContext";
import { useChatThreads } from "../Chatbot/hooks/useChatThreads";

const Navbar = () => {
  const { logout, user } = useContext(AuthContext);
  const { setThreadId, setThreadTitle, threadId, threadTitle } = useContext(
    ChatbotContext
  );

  const {
    deleteThread,
    refreshThreads,
    renameThread,
    threads,
  } = useChatThreads();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [newThreadTitle, setNewThreadTitle] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const styles = useStyles();

  const signOut = useCallback(() => {
    logout();
  }, [logout]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    refreshThreads();
  };

  const handleStartNewThread = () => {
    setThreadId(null); // Clear current thread ID
    if (setThreadTitle) {
      setThreadTitle(""); // Clear thread title
    }
  };

  const handleThreadSelect = (selectedThreadId: string) => {
    setThreadId(selectedThreadId);
    // Update thread title from the threads list
    const selectedThread = threads.find((t) => t.uuid === selectedThreadId);
    if (setThreadTitle && selectedThread) {
      setThreadTitle(selectedThread.title || "");
    }
  };

  const handleRename = () => {
    if (!threadId) return;
    const currentThread = threads.find((t) => t.uuid === threadId);
    const currentTitle = currentThread?.title || "";
    setNewThreadTitle(currentTitle);
    setIsEditingTitle(true);
  };

  const handleRenameConfirm = async () => {
    if (!threadId || !newThreadTitle.trim()) return;
    await renameThread(threadId.toString(), newThreadTitle.trim());
    setIsRenameDialogOpen(false);
    setNewThreadTitle("");
  };

  const handleTitleSubmit = async () => {
    if (!threadId || !newThreadTitle.trim()) {
      handleCancelEdit();
      return;
    }
    await renameThread(threadId.toString(), newThreadTitle.trim());
    // Update context thread title
    if (setThreadTitle) {
      setThreadTitle(newThreadTitle.trim());
    }
    setIsEditingTitle(false);
    setNewThreadTitle("");
  };

  const handleCancelEdit = () => {
    setIsEditingTitle(false);
    setNewThreadTitle("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleTitleSubmit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  const handleDelete = () => {
    if (!threadId) return;
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!threadId) return;
    await deleteThread(threadId.toString());
    setThreadId(null);
    setIsDeleteDialogOpen(false);
  };

  if (!user) return null;

  return (
    <>
      <div className={styles.navbar}>
        <div className={styles.leftSection}>
          <button
            aria-label="Open menu"
            className={styles.hamburgerButton}
            onClick={toggleSidebar}
            type="button"
          >
            <NavigationRegular />
          </button>
        </div>

        <div className={styles.centerSection}>
          {threadId && (
            <>
              {isEditingTitle ? (
                <Input
                  autoFocus
                  className={styles.titleInput}
                  onBlur={handleTitleSubmit}
                  onChange={(e) => setNewThreadTitle(e.target.value)}
                  onKeyDown={handleKeyDown}
                  value={newThreadTitle}
                />
              ) : (
                <Menu>
                  <MenuTrigger disableButtonEnhancement>
                    <MenuButton
                      appearance="transparent"
                      className={styles.threadMenuButton}
                    >
                      {threadTitle ||
                        getThreadTitle(threads, threadId?.toString() || null)}
                    </MenuButton>
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      {threads.length > 0 && (
                        <>
                          {/* <MenuItem onClick={handleRename}>
                            <div className={styles.actionOption}>
                              <Edit24Regular className={styles.actionIcon} />
                              {translate("Rename")}
                            </div>
                          </MenuItem> */}
                          <MenuItem onClick={handleStartNewThread}>
                            <div className={styles.actionOption}>
                              <AddSquareRegular className={styles.actionIcon} />
                              {translate("Start new thread")}
                            </div>
                          </MenuItem>
                          <MenuItem onClick={handleDelete}>
                            <div className={styles.actionOption}>
                              <Delete24Regular className={styles.actionIcon} />
                              {translate("Delete")}
                            </div>
                          </MenuItem>
                        </>
                      )}
                    </MenuList>
                  </MenuPopover>
                </Menu>
              )}
            </>
          )}
        </div>

        <div className={styles.rightSection}>
          {/* {threadId && (
            <Button
              appearance="subtle"
              aria-label={translate("Start new thread")}
              className={styles.newThreadButton}
              icon={<AddSquareRegular />}
              onClick={handleStartNewThread}
              size="small"
            ></Button>
          )} */}
        </div>
      </div>

      {/* Sidebar */}
      <Sidebar
        activeThreadId={threadId?.toString() || undefined}
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        onLogout={signOut}
        onStartNewThread={handleStartNewThread}
        onThreadSelect={handleThreadSelect}
        threads={threads}
        title="Menu"
      />

      {/* Delete Dialog */}
      <Dialog
        onOpenChange={(_, data) => setIsDeleteDialogOpen(data.open)}
        open={isDeleteDialogOpen}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{translate("Delete Thread")}</DialogTitle>
            <DialogContent>
              <p>
                {translate(
                  "Are you sure you want to delete this thread? This action cannot be undone."
                )}
              </p>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">{translate("Cancel")}</Button>
              </DialogTrigger>
              <Button appearance="primary" onClick={handleDeleteConfirm}>
                {translate("Delete")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

export default Navbar;

const useStyles = makeStyles({
  actionIcon: {
    height: "16px",
    width: "16px",
  },
  actionOption: {
    alignItems: "center",
    display: "flex",
    gap: "8px",
  },
  centerSection: {
    alignItems: "center",
    display: "flex",
    flex: "1",
    gap: "8px",
    justifyContent: "center",
    minWidth: "min-content",
  },
  dialogField: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginBottom: "16px",
  },
  dividerOption: {
    color: "#9CA3AF",
    cursor: "default",
    fontSize: "12px",
    textAlign: "center",
    userSelect: "none",
  },
  editingButton: {
    fontSize: "14px",
    justifyContent: "flex-end",
    textAlign: "right",
  },
  hamburgerButton: {
    "&:hover": {
      backgroundColor: "#F3F4F6",
    },
    alignItems: "center",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    padding: "8px",
  },
  leftSection: {
    alignItems: "center",
    display: "flex",
    flex: "0 0 auto",
    gap: "12px",
  },
  navbar: {
    alignItems: "center",
    borderBottom: "1px solid rgb(222, 222, 222)",
    display: "flex",
    flex: "0 auto 0",
    justifyContent: "center",
    minHeight: "0",
    padding: "6px 12px",
    position: "relative",
    width: "100%",
  },
  newThreadButton: {
    "&:hover": {
      backgroundColor: "#F3F4F6",
    },
    borderRadius: "6px",
    fontWeight: "500",
  },
  rightSection: {
    alignItems: "center",
    display: "flex",
    flex: "0 0 auto",
    gap: "12px",
  },
  threadMenuButton: {
    "& > span": {
      lineHeight: 0,
    },
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
  },
  titleInput: {
    textAlign: "center",
    width: "80%",
  },
});
